import { Close } from '@mui/icons-material';
import {
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
} from '@mui/material';
import { GenButton } from '@pinnacleautomotive/clarity-components';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export default function EditVehicleModal({
  open,
  title,
  onClose,
  vehicleDetails,
  loading,
  onSave,
  department,
  showPo = true,
  showRo = true,
}) {
  const { t } = useTranslation(['orders', 'global']);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();

  const onCloseHandle = () => {
    reset({ ...vehicleDetails });
    onClose();
  };

  useEffect(() => {
    if (loading) return;
    reset({ ...vehicleDetails });
  }, [reset, vehicleDetails, loading]);

  return (
    <Dialog open={open} maxWidth="xs" onClose={onCloseHandle}>
      <DialogTitle sx={{ background: theme => theme.palette.grey[50] }}>
        <Box display="flex" flexDirection="row" alignItems="center">
          <Typography variant="subtitle2" flexGrow={1}>
            {title}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={onCloseHandle}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: theme => theme.palette.grey[500],
            }}
          >
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={3} pt={2}>
          <Grid item xs={12}>
            <TextField
              label={t('global:vin')}
              required
              fullWidth
              size="small"
              disabled
              defaultValue={vehicleDetails?.vin}
              {...register('vin', {
                required: t('global:errors.required'),
                maxLength: {
                  value: 17,
                  message: t('global:errors.maxLength', { amount: '17' }),
                },
                pattern: {
                  value: /^[A-Z\d]+$/g,
                  message: t('global:errors.alphaNumericCapitalized'),
                },
                validate: v => v !== '',
              })}
              helperText={errors.vin?.message}
              error={!!errors.vin}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label={t('global:stock')}
              fullWidth
              size="small"
              required={department?.setting?.orders?.requiredFields.stockNumber}
              inputProps={{
                onChange: event =>
                  (event.target.value = event.target.value.toUpperCase()),
              }}
              defaultValue={vehicleDetails?.custody?.stockNumber}
              {...register('custody.stockNumber', {
                maxLength: {
                  value: 50,
                  message: t('global:errors.maxLength', { amount: '50' }),
                },
                pattern: /\S/,
                required: {
                  value:
                    department?.setting?.orders?.requiredFields.stockNumber,
                  message: t('global:errors.required'),
                },
              })}
              helperText={errors.custody?.stockNumber?.message}
              error={!!errors.custody?.stockNumber}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label={t('global:make')}
              required
              fullWidth
              size="small"
              defaultValue={vehicleDetails?.make}
              {...register('make', {
                required: t('global:errors.required'),
              })}
              helperText={errors.make?.message}
              error={!!errors.make}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label={t('global:model')}
              required
              fullWidth
              size="small"
              defaultValue={vehicleDetails?.model}
              {...register('model', {
                required: t('global:errors.required'),
              })}
              helperText={errors.model?.message}
              error={!!errors.model}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label={t('global:year')}
              type={'number'}
              required
              fullWidth
              size="small"
              defaultValue={vehicleDetails?.year}
              {...register('year', {
                valueAsNumber: true,
                required: t('global:errors.required'),
              })}
              helperText={errors.year?.message}
              error={!!errors.year}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label={t('global:color')}
              fullWidth
              size="small"
              defaultValue={vehicleDetails?.color}
              {...register('color')}
            />
          </Grid>
          {showRo && (
            <Grid item xs={6}>
              <TextField
                label={t('global:ro')}
                fullWidth
                required={
                  department?.setting?.orders?.requiredFields.repairOrder
                }
                size="small"
                defaultValue={vehicleDetails?.custody?.ro}
                {...register('ro', {
                  maxLength: {
                    value: 60,
                    message: t('global:errors.maxLength', { amount: '60' }),
                  },
                  required: {
                    value:
                      department?.setting?.orders?.requiredFields.repairOrder,
                    message: t('global:errors.required'),
                  },
                })}
                helperText={errors.ro?.message}
                error={!!errors.ro}
              />
            </Grid>
          )}
          {showPo && (
            <Grid item xs={6}>
              <TextField
                label={t('global:po')}
                fullWidth
                required={
                  department?.setting?.orders?.requiredFields.purchaseOrder
                }
                size="small"
                defaultValue={vehicleDetails?.custody?.po}
                {...register('po', {
                  maxLength: {
                    value: 32,
                    message: t('global:errors.maxLength', { amount: '32' }),
                    required: {
                      value:
                        department?.setting?.orders?.requiredFields
                          .purchaseOrder,
                      message: t('global:errors.required'),
                    },
                  },
                })}
                helperText={errors.po?.message}
                error={!!errors.po}
              />
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="flex-end"
          alignItems="flex-end"
          gap={2}
          p={2}
        >
          <GenButton
            text={t('global:genericButtons.cancel')}
            order={0}
            disabled={loading}
            onClick={onCloseHandle}
            sx={{
              color: theme => theme.palette.text.primary,
              borderColor: theme => theme.palette.divider,
            }}
          />
          <GenButton
            text={t('global:genericButtons.saveChanges')}
            order={1}
            color="primary"
            variant="contained"
            loading={loading}
            onClick={handleSubmit(onSave)}
          />
        </Box>
      </DialogActions>
    </Dialog>
  );
}
